import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { docTypeLabel } from '../../utils/qmsDocTypes';
import { AcknowledgeModal } from '../quality/QmsLibraryPage';

// ── Acknowledging a QMS document from the Actions card ───────────────────────
//
// When a version of a document that requires acknowledgement goes live, one
// Action is raised on everyone who has to read it. The item in the queue is not
// a pointer to the Quality Docs page: it opens the same reader the library
// uses, with the same rule that reaching the end of the full version is what
// records the acknowledgement. The reader posts the acknowledgement; the server
// then completes this person's part of the action, so nothing here writes
// anything itself.
//
// Mark done, Resolve all and Delete are withheld on the card for this kind of
// action, because each of them would clear the queue item and leave the
// acknowledgement unrecorded. Reading is the only way past it.

/**
 * @param documentId      the qms_documents id the action points at
 * @param canAcknowledge  whether this person is being asked (an assignee who has not yet done their part)
 * @param onAcknowledged  called once the acknowledgement lands, so the queue reloads
 */
export default function QmsAcknowledgeAction({ documentId, canAcknowledge, onAcknowledged }) {
  const [doc, setDoc] = useState(undefined);   // undefined = loading, null = not available to read
  const [reading, setReading] = useState(false);

  const load = useCallback(() => {
    api.get(`qms/documents/${documentId}`)
      .then(d => {
        // The detail carries the version list; the reader wants the live one flattened in.
        const live = (d.versions || []).find(v => v.id === d.current_version_id) || {};
        setDoc({ ...d, content_kind: live.content_kind, r2_key: live.r2_key, version_number: live.version_number });
      })
      .catch(() => setDoc(null));
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  if (doc === undefined) {
    return <div className="text-[9px] text-gray-400 mt-1.5">Loading the document…</div>;
  }

  // Archived, or taken back to draft, between the action being raised and now.
  if (doc === null) {
    return (
      <div className="mt-1.5 text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
        This document is no longer live, so there is nothing to acknowledge.
        <button onClick={load} className="ml-1 text-navy underline">Check again</button>
      </div>
    );
  }

  const acknowledged = !!doc.my_read;

  return (
    <div className="mt-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-1.5 flex-wrap text-[9px] text-gray-600">
        <span className="font-mono font-bold text-navy">{doc.doc_number}</span>
        <span className="text-[8px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{docTypeLabel(doc.doc_type)}</span>
        {doc.version_number && <span className="text-gray-400">v{doc.version_number}</span>}
      </div>
      {doc.description && (
        <div className="text-[9px] text-gray-600 mt-1 whitespace-pre-wrap break-words">{doc.description}</div>
      )}
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        {acknowledged ? (
          <span className="text-[9px] font-semibold text-emerald-700">✓ You acknowledged this on {formatDate(doc.my_read_at)}</span>
        ) : (
          <>
            <button onClick={() => setReading(true)}
              className="px-2.5 py-1 text-[9px] font-semibold bg-amber-500 text-white rounded hover:bg-amber-600">
              {canAcknowledge ? 'Read & acknowledge' : 'Read'}
            </button>
            {canAcknowledge && (
              <span className="text-[8px] text-gray-400">Read to the end of the {doc.content_kind === 'html' ? 'full version' : 'document'} to acknowledge it.</span>
            )}
          </>
        )}
      </div>
      {reading && (
        <AcknowledgeModal
          doc={doc}
          ackRequired={canAcknowledge && !acknowledged}
          onClose={() => setReading(false)}
          onAcknowledged={() => { load(); onAcknowledged && onAcknowledged(); }}
        />
      )}
    </div>
  );
}
